import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { ModalComponent } from '../../../../components/ModalComponent/ModalComponent';
import { useMonthlyTimesheetsByTimeRange } from '../../../../queries/hooks/useMonthlyTimesheetsByTimeRange';
import { DealSkeletonLoading } from '../../../../components/SkeletonLoader/DealSkeletonLoading';
import { ExportTimesheetsForm } from './ExportTimesheetsForm';
import { useMonthlyTimesheetsToExport } from '../../../../queries/hooks/useMonthlyTimesheetsToExport';
import { AccountantTSView, AvailableMonthlyTimesheet, EXPORT_MODE, UserRole } from '../../../../types';
import {
  generateActivityLogsTemplate,
  generateMonthTemplate,
  generateWeekTemplate,
  generateWeekdayTemplate,
} from '../../../../utils/excelMultiExportTimesheet';
import { useAllMonthlyTimesheets } from '../../../../queries/hooks/useAllMonthlyTimesheets';

type ModalProps = {
  onClose: () => void;
  isOpen: boolean;
  mode: EXPORT_MODE | null;
};

export interface IExportMultiTSForm {
  selectedPeriod: {
    year: number;
    months: {
      [month: number]: boolean;
    };
  }[];
  showDataBy: AccountantTSView | null;
  includeEmptyDeals: boolean;
}

const initialValues: IExportMultiTSForm = {
  selectedPeriod: [],
  showDataBy: null,
  includeEmptyDeals: false,
};

export function ExportMultiTimesheetModal({ isOpen, onClose, mode }: ModalProps) {
  const [timeRangeParams, setTimeRangeParams] = useState('');
  const [searchParams] = useSearchParams();
  const [triggerExport, setTriggerExport] = useState<AccountantTSView | null | 'none'>(null);
  const [includeEmptyDeals, setIncludeEmptyDeals] = useState(false);
  const ids = searchParams.get('ids') ?? '';

  const {
    isLoading: isLoadingTSAvailability,
    data: availablePeriods,
    isSuccess: isSuccessAvailablePeriods,
  } = useMonthlyTimesheetsByTimeRange(ids);

  const {
    isLoading: isLoaddingAllTSAvailability,
    data: allPeriods,
    isSuccess: isSuccessAllPeriods,
  } = useAllMonthlyTimesheets(ids);

  const isLoading =
    mode === EXPORT_MODE.TIMESHEETS ? isLoadingTSAvailability : isLoaddingAllTSAvailability;
  const isSuccess =
    mode === EXPORT_MODE.TIMESHEETS ? isSuccessAvailablePeriods : isSuccessAllPeriods;
  const availablePeriodsToShow = mode === EXPORT_MODE.TIMESHEETS ? availablePeriods : allPeriods;

  const { data: timesheets, isLoading: isLoadingTimesheets } = useMonthlyTimesheetsToExport({
    userIds: ids,
    timeRange: timeRangeParams,
    all: mode === EXPORT_MODE.ACTIVITY_LOGS,
    role: UserRole.ACCOUNTANT,
  });

  const handleExportClick = useCallback(
    async (showDataBy: AccountantTSView) => {
      if (timesheets) {
        if (showDataBy === AccountantTSView.month) {
          await generateMonthTemplate(timesheets, includeEmptyDeals);
        }
        if (showDataBy === AccountantTSView.week) {
          await generateWeekTemplate(timesheets, includeEmptyDeals);
        }
        if (showDataBy === AccountantTSView.weekday) {
          await generateWeekdayTemplate(timesheets, includeEmptyDeals);
        }
        onClose();
      }
    },
    [onClose, timesheets, includeEmptyDeals]
  );

  const handleActivityLogExportClick = useCallback(async () => {
    if (timesheets) {
      generateActivityLogsTemplate(timesheets);
      onClose();
    }
  }, [onClose, timesheets]);

  useEffect(() => {
    if (timesheets && triggerExport) {
      if (triggerExport === 'none') {
        handleActivityLogExportClick();
      } else {
        handleExportClick(triggerExport as AccountantTSView);
      }
      setTriggerExport(null);
    }
  }, [triggerExport, handleActivityLogExportClick, timesheets, handleExportClick]);

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={async (values: IExportMultiTSForm) => {
        const queryParams = values.selectedPeriod
          .flatMap(({ year, months }) =>
            Object.entries(months)
              // eslint-disable-next-line @typescript-eslint/no-unused-vars
              .filter(([_, value]) => value)
              .map(([month]) => `${month}-${year}`)
          )
          .join(',');
        setTimeRangeParams(queryParams);
        setIncludeEmptyDeals(values.includeEmptyDeals);
        setTriggerExport(values.showDataBy ?? 'none');
      }}
    >
      {({ handleSubmit, values }) => {
        const isTimesheetFormDisabled =
          values.selectedPeriod.every(({ months }) => Object.values(months).every((val) => !val)) ||
          !values.showDataBy;
        const isActivityLogFormDisabled = values.selectedPeriod.every(({ months }) =>
          Object.values(months).every((val) => !val)
        );
        const isFormDisabled =
          mode === EXPORT_MODE.TIMESHEETS ? isTimesheetFormDisabled : isActivityLogFormDisabled;

        return (
          <Form>
            <ModalComponent
              isOpen={isOpen}
              onClose={onClose}
              actionButtonsProps={{
                isSubmitDisabled: isFormDisabled,
                onSubmit: handleSubmit,
                onCancel: onClose,
                submitText: isLoadingTimesheets ? 'Exporting...' : 'Export',
              }}
              title={`Export Timesheet`}
              top='80px'
            >
              <>
                {isLoading && <DealSkeletonLoading />}
                {isSuccess && (
                  <ExportTimesheetsForm
                    availablePeriods={availablePeriodsToShow as AvailableMonthlyTimesheet[]}
                    hideDataBySelector={mode === EXPORT_MODE.ACTIVITY_LOGS}
                  />
                )}
              </>
            </ModalComponent>
          </Form>
        );
      }}
    </Formik>
  );
}
